import { computed, readonly, ref, type Ref } from 'vue'
import { cloneDeep, isEqualWith, isEqual, keyBy, merge, mergeWith } from 'lodash-es'

import {
  CanvasBuilderMode,
  ComponentTypes,
  CanvasHistoryAction,
  type CanvasBuilderProps,
  type CanvasTheme,
  type ComponentNode,
  type ContentGridProps,
  type CRMBuilderProps,
  type SectionComponentOverrides,
  type SectionListProps,
  type MultimediaProps,
  type ContentListProps,
} from '@canvas-builder/types/canvas'
import { filterTreeBy, findNodeInTreeById, findParentByNodeId, regenerateTreeIds } from '@canvas-builder/util/tree'
import { type CanvasRetrieve, type FileRetrieve, type CanvasSection, type User } from '@openapi'
import { simpleDeepClone } from '@canvas-builder/util/clone'
import {
  addCanvasComponent,
  isHeadlessOrNotAvailableApp,
  shouldDisplayPlaceholderComponent,
  handleThemeAssetComparison,
  processCanvasForSectionThemeOverride,
} from '@canvas-builder/util/canvas.util'
import { CANVAS_WIDTH, componentMeta, SERVER_ONLY_PROPS } from '@canvas-builder/constants'
import { CanvasActions } from '@lib/types/canvasActions'
import type { EmbedLocation } from '@lib/types/app'
import useAppEmit from '@lib/composables/appEmit.use'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useCanvasHistory from '@canvas-builder/composables/useCanvasHistory'
import useCanvasSectionOverrides from '@canvas-builder/composables/useCanvasSectionOverrides'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import useReorderComponent from '@canvas-builder/composables/useReorderComponent'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvasBlocks from '@canvas-builder/composables/useCanvasBlocks'
import { applyOverridesToNode } from '@canvas-builder/components/ui/Block/Block.helpers'
import { DEFAULT_GLOBAL_COMPONENT_SPACING } from '@canvas-builder/constants/theme.const'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CanvasMetadataRetrieve } from '@lib/types/canvases'
import type {
  BackgroundAttachment,
  BackgroundPositionX,
  BackgroundPositionY,
  BackgroundRepeat,
  BackgroundSize,
} from '@lib/types/background'
import { navigateToHome as navigateToHomeHelper } from '@canvas-builder/App.helpers'
import useCanvasNodes from '@canvas-builder/composables/useCanvasNodes'
import useCanvasThemeAssets from '@canvas-builder/composables/useCanvasThemeAssets'
import type { ActionCallback } from '@collection-player/composables/keyboard-bindings.use.ts'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import { convertToPixels } from '@canvas-builder/util/print.util'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import type useAnnotation from '@canvas-builder/composables/useAnnotation.ts'
import useTemporaryContext from '@canvas-builder/composables/useTemporaryContext.ts'
import useContentStore from '@canvas-builder/components/ui/ContentList/content.store.ts'

const { appEmit } = useAppEmit()
const {
  backgroundAttachment,
  backgroundImage,
  backgroundPositionX,
  backgroundPositionY,
  backgroundRepeat,
  backgroundSize,
  backgroundVideoUrl,
  canvasBgColor,
  globalComponentSpacing,
  initializeTheme,
  inheritThemeFromParent,
  isFluid,
  maintainSectionTheme,
  palette,
  logo,
  assets,
  aspectRatio,
  removeCssUrlFunc,
  setFontAndFetchAsset,
  typography,
  loadSectionThemesAssets,
  loadSectionThemes,
  pdfDimensionsPreset,
  pdfDimensionsWidth,
  pdfDimensionsHeight,
} = useCanvasTheme()
const {
  overrideSectionComponentData,
  sectionComponentOverrides,
  cleanup: cleanupOverrides,
} = useCanvasSectionOverrides()

const {
  nodeStateCleanup: cleanupNodeState,
  updateMultimediaNodeWithThemeAsset: updateNodeWithThemeAsset,
  processCanvasNodesForDisplay,
  findNodeInSection: findNodeInSectionById,
  updateNodeInSection: updateNodeInSectionById,
  nodeStateInitialize,
} = useCanvasNodes()
const { clearManuallyChangedThemeAssets } = useCanvasThemeAssets()

// helpers
function devLog(...args: any[]) {
  if (import.meta.env.DEV) {
    console.log(...args)
  }
}

function isContentEqual<T = ComponentNode[] | SectionComponentOverrides>(a: T, b: T): any {
  const excludeKeys = SERVER_ONLY_PROPS as readonly string[] // read-only / server set keys
  const contentGridExcludeKeys = ['url'] // Exclude 'url' for ContentGrid type nodes
  const shouldExcludeThemeAssets = areThemeAssetsEnabled.value // Check if theme assets should be excluded from comparison

  // File-related properties we want to ignore in theme assets
  const fileProps = ['id', 'url', 'content_url', 'name', 'thumbnail_url', 'external_url']

  // Store IDs of components with theme_meta to avoid repeated processing
  const componentsWithThemeMeta = new Set<string>()

  // Pre-process objects to identify components with theme_meta
  if (shouldExcludeThemeAssets) {
    const findComponentsWithThemeMeta = (nodes: any) => {
      if (!nodes) return

      // Process array of nodes
      if (Array.isArray(nodes)) {
        nodes.forEach((node) => findComponentsWithThemeMeta(node))
        return
      }

      // Process individual node
      if (nodes && typeof nodes === 'object') {
        // Check if this is a multimedia component with theme_meta
        if (nodes.type === 'Multimedia' && nodes.theme_meta && nodes.id) {
          componentsWithThemeMeta.add(nodes.id)
        }

        // Process children recursively
        if (nodes.children && Array.isArray(nodes.children)) {
          findComponentsWithThemeMeta(nodes.children)
        }

        // Also check for nodes in data.children if it exists (for nested structures)
        if (nodes.data && nodes.data.children && Array.isArray(nodes.data.children)) {
          findComponentsWithThemeMeta(nodes.data.children)
        }
      }
    }

    // Process both sides of the comparison
    findComponentsWithThemeMeta(a)
    findComponentsWithThemeMeta(b)
  }

  return isEqualWith(a, b, (objValue, othValue, key, object, other) => {
    // If the key is in the exclude list, treat as equal
    if (key && typeof key === 'string') {
      if (excludeKeys.includes(key)) {
        return true
      }

      // Special handling for ContentGrid type nodes
      if (key === 'type' && (objValue === 'ContentGrid' || othValue === 'ContentGrid')) {
        const objParent = object || {}
        if (objParent && contentGridExcludeKeys.some((k) => k in objParent)) {
          return true
        }
      }

      // Special handling for theme asset multimedia components
      if (shouldExcludeThemeAssets) {
        return handleThemeAssetComparison(
          key,
          objValue,
          othValue,
          object,
          other,
          fileProps,
          isEqual,
          componentsWithThemeMeta,
        )
      }
    }

    // Fall through to default equality comparison
    return undefined
  })
}

function getAvailableSections(data: SectionListProps) {
  return (data?.sections ?? []).reduce(
    (sections, section) => {
      const foundSection = activeCanvas.value?.sections?.find?.((s) => s.id === section.id)

      if (foundSection) {
        sections.push(foundSection)
      }

      return sections
    },
    <CanvasSection[]>[],
  )
}

// cleanup
function cleanup() {
  inCall.value = undefined
  mode.value = undefined
  displayMode.value = 'default'
  startPage.value = undefined
  endPage.value = undefined
  isCRM.value = false
  isEditMode.value = true
  addMode.value = false
  componentEditMode.value = false
  componentSettingsMode.value = false
  componentSelectedMode.value = false
  isRedirectEvent.value = false
  activeCanvas.value = null
  canvasContent.value = []
  canvasHomePage.value = null
  justAddedComponentId.value = null
  contentRef.value = null
  activeNavigationItems.value = []
  myUserId.value = null
  isFullscreen.value = false
  onSfdcAccountSearch.value = () => devLog(`onSfdcAccountSearch cleaned up`)
  onSave.value = () => {
    devLog(`onSave cleaned up`)
    return Promise.resolve()
  }
  onDownloadFile.value = () => devLog(`onDownloadFile cleaned up`)
  onDownloadCanvas.value = () => devLog(`onDownloadCanvas cleaned up`)
  onExit.value = () => devLog(`onExit cleaned up`)
  onFileOpen.value = () => devLog(`onFileOpen cleaned up`)
  fetchFile.value = () => devLog(`fetchFile cleaned up`)
  listFiles.value = () => devLog(`listFiles cleaned up`)
  listRecommendedFiles.value = () => devLog(`listRecommendedFiles cleaned up`)
  renderPageAsImageUrl.value = () => devLog(`renderPageAsImageUrl cleaned up`)
  onComponentEnter.value = () => devLog(`onComponentEnter cleaned up`)
  onComponentExit.value = () => devLog(`onComponentExit cleaned up`)
  onAppEnter.value = () => devLog(`onAppEnter cleaned up`)
  onAppExit.value = () => devLog(`onAppExit cleaned up`)
  getEnv.value = () => devLog(`getEnv cleaned up`)
  onTrackShare.value = undefined
  share.value = undefined
  builderCache.value = {}
  pitcherSettings.value = {}
  clearTemporaryContext()

  // Clear file data cache
  fileDataCache.value.clear()

  // composition cleanup
  cleanupOverrides()

  // Clean up canvas node state
  cleanupNodeState()

  // Clear manually changed theme assets
  clearManuallyChangedThemeAssets()
}

// modes
const inCall = ref<boolean | undefined>(undefined)
const mode = ref<CanvasBuilderProps['mode'] | undefined>()
const displayMode = ref<CanvasBuilderProps['displayMode']>('default')
const startPage = ref<CanvasBuilderProps['startPage']>()
const endPage = ref<CanvasBuilderProps['endPage']>()
const isCRM = ref(false)
const isEditMode = ref(true)
const addMode = ref<
  false | { type: ComponentTypes; parentId: ComponentNode['id'] | null; afterSiblingId: ComponentNode['id'] | null }
>(false)
const componentEditMode = ref<
  false | { type: ComponentTypes; id: ComponentNode['id'] | null; usedInSectionId?: CanvasRetrieve['id'] }
>(false)
const componentSettingsMode = ref<false | ComponentNode['id']>(false)
const componentSelectedMode = ref<false | { id: ComponentNode['id']; navigatorItemIndex?: number }>(false)
const isRedirectEvent = ref(false)

// data
const isCanvasPreviewMode = ref(false)

const activeCanvas = ref<CanvasRetrieve | null>(null)
const areThemesEnabled = ref(false)
const enableImprovedCanvasNodeUpdating = ref(false)
const areThemeAssetsEnabled = ref(false)
const contentAnnotation = ref<ReturnType<typeof useAnnotation> | null>(null)
const canvasContent = ref<ComponentNode[]>([])
const canvasHomePage = ref<{ component_id: ComponentNode['id']; section_id?: CanvasRetrieve['id'] | null } | null>(null)
const canvasMetadata = ref<CanvasMetadataRetrieve['config'] | null>(null)
const fileDataCache = ref<Map<string, any>>(new Map())
const justAddedComponentId = ref<ComponentNode['id'] | null>(null)
const contentRef = ref<{ $el: HTMLElement } | null>(null)
const activeNavigationItems = ref<{ id: string; sectionId?: string; previewMode?: boolean; index?: number }[]>([])
const myUserId = ref<User['id'] | null>(null)
const isFullscreen = ref(false)
const visibleCollectionPlayerRefs = ref(
  new Set<Ref<{ isFullscreen: Ref<boolean>; handleKeyAction: ActionCallback; element: HTMLElement } | null>>(),
)
const builderCache = ref<Record<string, any>>({})
const pitcherSettings = ref<OrganizationSettings & InstanceSettings>({})

const { setTemporaryContextKeys, clearTemporaryContext, getSafeContext, temporaryContextKeys, watchCanvas } =
  useTemporaryContext(pitcherSettings, activeCanvas)

// getters
export type ComponentNodesById = typeof componentNodesById

const activeCanvasSections = computed(() => keyBy(activeCanvas.value?.sections, 'id'))

const areTokenConstraintsActive = computed(() => {
  return isAdmin.value ? false : !activeCanvas.value?.is_section
})

const componentNodesById = computed(() => {
  const flattenTree = (node: ComponentNode, map: Record<ComponentNode['id'], ComponentNode>) => {
    map[node.id] = node
    node.children?.forEach((child) => flattenTree(child, map))
  }
  const nodeMap: Record<ComponentNode['id'], ComponentNode> = {}
  canvasContent.value.forEach((rootNode) => flattenTree(rootNode, nodeMap))

  return readonly(nodeMap)
})
const isAdmin = computed(() => mode.value === 'admin')
const isImpact = computed(() => mode.value === 'impact')
const isDsr = computed(() => mode.value === 'sharing')
const parentIdByNodeId = computed(() => {
  const buildParentMap = (
    node: ComponentNode,
    parentMap: Record<ComponentNode['id'], ComponentNode['id'] | null>,
    parentId: ComponentNode['id'] | null = null,
  ) => {
    parentMap[node.id] = parentId
    node.children?.forEach((child) => buildParentMap(child, parentMap, node.id))
  }

  const parentMap: Record<ComponentNode['id'], ComponentNode['id'] | null> = {}
  canvasContent.value.forEach((rootNode) => buildParentMap(rootNode, parentMap))

  return readonly(parentMap)
})

export type SectionComponentNodesBySectionId = typeof sectionComponentNodesBySectionId
const sectionComponentNodesBySectionId = computed(() => {
  const flattenTree = (node: ComponentNode, map: Record<ComponentNode['id'], ComponentNode>) => {
    map[node.id] = node
    node.children?.forEach((child) => flattenTree(child, map))
  }

  const sectionNodeMap: Record<CanvasRetrieve['id'], Record<ComponentNode['id'], ComponentNode>> = {}
  activeCanvas.value?.sections.forEach((section) => {
    const nodeMap: Record<ComponentNode['id'], ComponentNode> = {}
    section.content.data.forEach((rootNode: ComponentNode) => flattenTree(rootNode, nodeMap))
    sectionNodeMap[section.id] = nodeMap
  })

  return readonly(sectionNodeMap)
})

const isCanvas = computed(
  () =>
    !!(
      activeCanvas.value &&
      !activeCanvas.value?.is_template &&
      !activeCanvas.value?.is_section &&
      !activeCanvas.value?.is_block
    ),
)
const isCanvasTemplate = computed(
  () =>
    !!(
      activeCanvas.value &&
      activeCanvas.value?.is_template &&
      !activeCanvas.value?.is_section &&
      !activeCanvas.value?.is_block
    ),
)
const isTemplate = computed(() => !!activeCanvas.value?.is_template)
const isSection = computed(() => !!activeCanvas.value?.is_section)
const isBlock = computed(() => !!activeCanvas.value?.is_block)

const isContentSameAsSaved = computed(() =>
  isContentEqual<ComponentNode[]>(activeCanvas.value?.content?.data ?? [], canvasContent.value),
)
const areOverridesSameAsSaved = computed(() =>
  isContentEqual<SectionComponentOverrides>(
    activeCanvas.value?.content?.overrides ?? {},
    sectionComponentOverrides.value,
  ),
)

const isHomePageSameAsSaved = computed(() => {
  const savedHomePage = activeCanvas.value?.content?.home_page || null
  const currentHomePage = canvasHomePage.value

  return isEqual(savedHomePage, currentHomePage)
})

const isSameAsSaved = computed(
  () => isContentSameAsSaved.value && areOverridesSameAsSaved.value && isHomePageSameAsSaved.value,
)
const isViewOnlyMode = computed(
  () =>
    mode.value === CanvasBuilderMode.IMPACT && isSection.value && activeCanvas.value?.created_by.id !== myUserId.value,
)
const singletonsInCanvasMap = computed(() => {
  const singletonComponentTypes = Object.entries(componentMeta)
    .filter(([, meta]) => meta.singleton)
    .map(([k]) => k)

  return singletonComponentTypes.reduce(
    (acc, type) => ({ ...acc, [type]: !!filterTreeBy(canvasContent.value, (n) => n.type === type).length }),
    {} as Record<ComponentTypes, boolean>,
  )
})
const selectedComponentType = computed(() => {
  if (!componentSelectedMode.value) return null

  return componentNodesById.value[componentSelectedMode.value.id]?.type ?? null
})

const activeEditNode = computed(() => {
  if (!componentEditMode.value || !componentEditMode.value.id) return null
  else if (componentEditMode.value.usedInSectionId)
    return sectionComponentNodesBySectionId.value[componentEditMode.value.usedInSectionId]?.[componentEditMode.value.id]
  return componentNodesById.value[componentEditMode.value.id]
})

const activeEditNodeParentType = computed(() => {
  if (!activeEditNode.value?.id || !canvasContent.value) return undefined
  const parent = findParentByNodeId(canvasContent.value, activeEditNode.value.id)
  return parent?.type
})

const selectedComponentParent = computed(() => {
  if (!componentSelectedMode.value || !canvasContent.value) return null
  return findParentByNodeId(canvasContent.value, componentSelectedMode.value.id)
})

const selectedComponentParentType = computed(() => {
  return selectedComponentParent.value?.type
})

const activeSettingsNode = computed(() => {
  const activeComponentId = componentSettingsMode.value

  if (!activeComponentId) return null

  return componentNodesById.value[activeComponentId] ?? null
})

const navigationItems = computed(() => {
  const { getVisibleCanvasContent, getVisibleKey } = useCanvasVisibility()
  const visibleKey = getVisibleKey()

  return (
    getVisibleCanvasContent(cloneDeep(canvasContent.value), visibleKey).reduce(
      (list, component) => {
        if (isHeadlessOrNotAvailableApp(component, isEditMode.value, currentEmbedLocation.value)) {
          return list
        } else if ([ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type)) {
          if (shouldDisplayPlaceholderComponent(isEditMode.value, mode.value, component?.visible)) {
            list.push({
              id: component.id,
              name: component.tracking_id ?? component.type,
              type: component.type === ComponentTypes.ContentList ? 'Section' : component.type, // with ContentList use same type as SectionList
              uniqueId: `Nav-${component.id}`,
              metadata: {},
            })

            return list
          }

          const { data } = component as ComponentNode<SectionListProps>

          if (!data) return list

          const availableSections = getAvailableSections(data)
          const { files } = useContentStore()

          list.push(
            ...(availableSections.map((section, index) => {
              let name = section.name
              let pageIndex = undefined

              let file: FileRetrieve | undefined = undefined

              if (component.type === ComponentTypes.ContentList) {
                const contentListData = component?.data as ContentListProps | undefined
                const fileId = contentListData?.sections?.[index]?.file_id

                if (fileId && files.loaded?.[fileId]) {
                  file = files.loaded?.[fileId]
                  pageIndex = contentListData?.sections?.[index]?.page_index

                  const newName = files.loaded?.[fileId]?.name

                  if (newName) {
                    name = newName
                  }
                }
              }

              return {
                sectionId: section?.id,
                id: component.id,
                name,
                pageIndex,
                thumbnailUrl: file
                  ? file?.content_thumbnails?.[pageIndex ?? 0]
                  : (section.metadata?.thumbnail ?? (section as any)?.thumbnail_url ?? ''),
                type: 'Section' as const,
                uniqueId: `Nav-${component.id}-${section.id}`,
                metadata: section.metadata,
                sectionIndex: index,
              }
            }) ?? []),
          )

          if (isEditMode.value && !availableSections?.length) {
            list.push({
              id: component.id,
              name: '',
              type: 'Section' as const,
              uniqueId: `Nav-${component.id}`,
              metadata: {},
            })
          }
        } else {
          list.push({
            id: component.id,
            name: component.tracking_id ?? component.type,
            type: component.type,
            uniqueId: `Nav-${component.id}`,
            metadata: component.data, // allow searching in component node data (e.g. file names in content grids)
          })
        }

        return list
      },
      [] as {
        id: string
        name: string
        type: ComponentTypes | 'Section'
        sectionId?: string
        thumbnailUrl?: string
        uniqueId: string
        metadata?: Record<string, any>
        sectionIndex?: number
        pageIndex?: number
      }[],
    ) ?? []
  )
})

const currentEmbedLocation = computed(
  () => (mode.value === CanvasBuilderMode.SHARING ? 'dsr' : 'canvas') satisfies EmbedLocation,
)

// callbacks
const onSfdcAccountSearch: Ref<CRMBuilderProps['onSfdcAccountSearch'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No onSfdcAccountSearch function provided`),
)
const onSave: Ref<CanvasBuilderProps['onSave'] | ((...data: any[]) => Promise<void>)> = ref(() => {
  devLog(`No onSave function provided`)
  return Promise.resolve()
})
const onDownloadFile: Ref<CanvasBuilderProps['onDownloadFile'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No onDownloadFile function provided`),
)
const onDownloadCanvas: Ref<CanvasBuilderProps['onDownloadCanvas'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No onDownloadCanvas function provided`),
)
const onExit: Ref<CanvasBuilderProps['onExit']> = ref(() => devLog(`No onExit function provided`))
const onFileOpen: Ref<CanvasBuilderProps['onFileOpen']> = ref(() => devLog(`No onFileOpen function provided`))
const onFileEnter: Ref<CanvasBuilderProps['onFileEnter']> = ref(() => devLog(`No onFileEnter function provided`))
const onWizardEnter: Ref<CanvasBuilderProps['onWizardEnter']> = ref(() => devLog(`No onWizardEnter function provided`))
const onWizardExit: Ref<CanvasBuilderProps['onWizardExit']> = ref(() => devLog(`No onWizardExit function provided`))
const onFileExit: Ref<CanvasBuilderProps['onFileExit']> = ref(() => devLog(`No onFileExit function provided`))
const onFilePageEnter: Ref<CanvasBuilderProps['onFilePageEnter']> = ref(() =>
  devLog(`No onFilePageEnter function provided`),
)
const onFilePageExit: Ref<CanvasBuilderProps['onFilePageExit']> = ref(() =>
  devLog(`No onFilePageExit function provided`),
)
const onCollectionPlayerContentChange: Ref<CanvasBuilderProps['onCollectionPlayerContentChange']> = ref(() =>
  devLog(`No onCollectionPlayerContentChange function provided`),
)
const fetchFile: Ref<CanvasBuilderProps['fetchFile'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No fetchFile function provided`),
)
const listFiles: Ref<CanvasBuilderProps['listFiles'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No listFiles function provided`),
)
const isSetListFilesApi = ref(false)
const listRecommendedFiles: Ref<CanvasBuilderProps['listRecommendedFiles'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No listRecommendedFiles function provided`),
)
const renderPageAsImageUrl: Ref<
  CanvasBuilderProps['renderPageAsImageUrl'] | ((...data: any[]) => Promise<ArrayBuffer> | void)
> = ref(() => devLog(`No renderPageAsImageUrl function provided`))

const onComponentEnter: Ref<CanvasBuilderProps['onComponentEnter'] | ((c: ComponentNode | CanvasRetrieve) => void)> =
  ref(() => devLog(`No onComponentEnter function provided`))

const onComponentExit: Ref<CanvasBuilderProps['onComponentExit'] | ((c: ComponentNode | CanvasRetrieve) => void)> = ref(
  () => devLog(`No onComponentExit function provided`),
)

const onAppEnter: Ref<CanvasBuilderProps['onAppEnter'] | ((c: { src: string }) => void)> = ref(() =>
  devLog(`No onAppEnter function provided`),
)

const onAppExit: Ref<CanvasBuilderProps['onAppExit'] | ((c: { src: string }) => void)> = ref(() =>
  devLog(`No onAppExit function provided`),
)

const onPageEnter: Ref<CanvasBuilderProps['onPageEnter'] | ((c: ComponentNode[]) => void)> = ref(() =>
  devLog(`No onPageEnter function provided`),
)
const onPageExit: Ref<CanvasBuilderProps['onPageExit'] | ((c: ComponentNode[]) => void)> = ref(() =>
  devLog(`No onPageExit function provided`),
)
const getEnv: Ref<CanvasBuilderProps['getEnv'] | ((...data: any[]) => void)> = ref(() =>
  devLog(`No getEnv function provided`),
)

const onTrackShare = ref<CanvasBuilderProps['onTrackShare']>()
const share = ref<CanvasBuilderProps['share']>()

// actions
const initialize = (props: CanvasBuilderProps, launchDarkly: LaunchDarklyEnv) => {
  if (props.displayMode) {
    displayMode.value = props.displayMode
  }

  isCanvasPreviewMode.value = window.location.hash.includes('canvas-preview-mode=true')
  areThemesEnabled.value = !!launchDarkly.enable_themes && !!props.theme
  enableImprovedCanvasNodeUpdating.value = !!launchDarkly.enable_improved_canvas_node_updating
  areThemeAssetsEnabled.value = areThemesEnabled.value && !!launchDarkly.enable_theme_assets

  startPage.value = props.startPage
  endPage.value = props.endPage

  mode.value = props.mode
  isCRM.value = props.isCRM

  useCanvasPages().reset()
  props.onSfdcAccountSearch && (onSfdcAccountSearch.value = props.onSfdcAccountSearch)
  props.onExit && (onExit.value = props.onExit)
  props.onDownloadFile && (onDownloadFile.value = props.onDownloadFile)
  props.onDownloadCanvas && (onDownloadCanvas.value = props.onDownloadCanvas)
  props.onComponentEnter && (onComponentEnter.value = props.onComponentEnter)
  props.onComponentExit && (onComponentExit.value = props.onComponentExit)
  props.onAppEnter && (onAppEnter.value = props.onAppEnter)
  props.onAppExit && (onAppExit.value = props.onAppExit)
  props.onPageEnter && (onPageEnter.value = props.onPageEnter)
  props.onPageExit && (onPageExit.value = props.onPageExit)
  props.onSave &&
    (onSave.value = (
      canvasId: Parameters<NonNullable<CanvasBuilderProps['onSave']>>[0],
      canvasData: Parameters<NonNullable<CanvasBuilderProps['onSave']>>[1],
      showToast?: Parameters<NonNullable<CanvasBuilderProps['onSave']>>[2],
    ) => props.onSave(canvasId, canvasData, showToast))
  props.onFileOpen && (onFileOpen.value = props.onFileOpen)
  props.onFileEnter && (onFileEnter.value = props.onFileEnter)
  props.onWizardEnter && (onWizardEnter.value = props.onWizardEnter)
  props.onWizardExit && (onWizardExit.value = props.onWizardExit)
  props.onFileExit && (onFileExit.value = props.onFileExit)
  props.onFilePageEnter && (onFilePageEnter.value = props.onFilePageEnter)
  props.onFilePageExit && (onFilePageExit.value = props.onFilePageExit)
  props.onCollectionPlayerContentChange &&
    (onCollectionPlayerContentChange.value = props.onCollectionPlayerContentChange)
  props.fetchFile && (fetchFile.value = props.fetchFile)
  if (props.listFiles) {
    listFiles.value = props.listFiles
    isSetListFilesApi.value = true
  } else {
    isSetListFilesApi.value = false
  }
  props.listRecommendedFiles && (listRecommendedFiles.value = props.listRecommendedFiles)
  props.renderPageAsImageUrl && (renderPageAsImageUrl.value = props.renderPageAsImageUrl)
  props.getEnv && (getEnv.value = props.getEnv)
  props.share && (share.value = props.share)
  props.onTrackShare && (onTrackShare.value = props.onTrackShare)
  props.myUserId && (myUserId.value = props.myUserId)
  props.inCall && (inCall.value = props.inCall)
  props.isRedirectEvent && (isRedirectEvent.value = props.isRedirectEvent)

  setActiveCanvas(cloneDeep(props.activeCanvas))
  // in impact show my saved changes for blocks
  if (props.activeCanvas?.is_block && mode.value === CanvasBuilderMode.IMPACT) {
    replaceWithMyBlockOverrides()
  }

  if (props.mode !== 'sharing') {
    switch (props.metadata.startInMode) {
      case CanvasActions.Edit:
        isEditMode.value = true
        break
      case CanvasActions.Present:
        isEditMode.value = false
        break
      default:
        break
    }
  } else {
    isEditMode.value = false
  }

  if (isCanvasPreviewMode.value) {
    isFullscreen.value = true
    isEditMode.value = false
    enterFullscreen({ skipJsApiEvent: true })
  } else if (isEditMode.value === false && props.isFullscreen) {
    enterFullscreen()
  }

  useReorderComponent().initialize()

  // Initialize canvas node state with current state
  nodeStateInitialize(
    Object.fromEntries(Object.entries(componentNodesById.value).map(([key, value]) => [key, { ...value }])) as any,
    updateNodeDataById,
  )
}
const setAddMode = (
  type: false | ComponentTypes,
  parentId: ComponentNode['id'] | null = null,
  afterSiblingId: ComponentNode['id'] | null = null,
) => {
  addMode.value = type ? { type, parentId, afterSiblingId } : type
}

const setComponentEditMode = (
  type: false | ComponentTypes,
  id: ComponentNode['id'] | null = null,
  usedInSectionId?: CanvasRetrieve['id'],
) => {
  componentEditMode.value = type ? { type, id, usedInSectionId } : type
}

const setComponentSettingsMode = (id: ComponentNode['id'] | false = false) => {
  componentSettingsMode.value = id
}

const setComponentSelectedMode = (id?: ComponentNode['id'], navigatorItemIndex?: number) => {
  componentSelectedMode.value = id ? { id, navigatorItemIndex } : false
}

const updateActiveCanvasProperties = (newProperties: Partial<CanvasRetrieve>) => {
  if (!activeCanvas.value) return
  activeCanvas.value = { ...activeCanvas.value, ...cloneDeep(newProperties) }
}

const setLegacyTheme = async (theme?: CanvasTheme | null) => {
  const themeVars = useThemeVars()
  if (!theme) {
    initializeTheme()
    return
  }
  backgroundAttachment.value = (theme.style?.backgroundAttachment as BackgroundAttachment) ?? 'fixed'
  backgroundPositionX.value = (theme.style?.backgroundPositionX as BackgroundPositionX) ?? 'center'
  backgroundPositionY.value = (theme.style?.backgroundPositionY as BackgroundPositionY) ?? 'center'
  backgroundRepeat.value = (theme.style?.backgroundRepeat as BackgroundRepeat) ?? 'no-repeat'
  backgroundSize.value = (theme.style?.backgroundSize as BackgroundSize) ?? 'auto'
  backgroundImage.value = removeCssUrlFunc(theme.style?.backgroundImage ?? '')
  backgroundVideoUrl.value = theme.background_video_url ?? ''
  canvasBgColor.value = theme.canvas_bg_color ?? ''
  globalComponentSpacing.value = theme.global_component_spacing ?? DEFAULT_GLOBAL_COMPONENT_SPACING
  inheritThemeFromParent.value = theme.inherit_theme_from_parent ?? false
  isFluid.value = theme.fluid ?? false
  maintainSectionTheme.value = theme.maintain_section_theme ?? true
  palette.value = theme.palette ?? [themeVars.value.primary]
  logo.value = theme.logo ?? null
  assets.value = theme.assets ?? []
  aspectRatio.value = theme.aspect_ratio ?? 'unlocked'
  setFontAndFetchAsset(theme.font?.name, theme.font?.url)
  typography.value = theme.typography ?? {}
  pdfDimensionsPreset.value = theme.pdf_dimensions?.preset
  pdfDimensionsWidth.value = theme.pdf_dimensions?.width
  pdfDimensionsHeight.value = theme.pdf_dimensions?.height
}

async function replaceWithMyBlockOverrides() {
  // preview the block as it would look when i add it in my canvases
  if (!activeCanvas.value) return
  const { getUserBlockOverrides } = useCanvasBlocks()
  const overrides = await getUserBlockOverrides(activeCanvas.value.id)
  if (!overrides) return

  const contentDataResult = canvasContent.value.map((node) => applyOverridesToNode(node, overrides))

  canvasContent.value = contentDataResult
  setActiveCanvas({
    ...activeCanvas.value,
    content: {
      ...activeCanvas.value.content,
      data: contentDataResult,
    },
  })
}

const setActiveCanvas = (newCanvas: CanvasRetrieve) => {
  const { resetCanvasHistory } = useCanvasHistory()

  try {
    let data = newCanvas?.content?.data ? simpleDeepClone(newCanvas.content).data : []

    // Process the data to ensure multimedia components are properly handled
    data = processCanvasNodesForDisplay(data as ComponentNode[])

    if (data.length > 0) {
      updateFileDataDict(data as ComponentNode[]).catch((error: any) => {
        console.warn("Could not fetch latest extra file data for this canvas's files:", error)
      })
    }

    if (!areThemesEnabled.value) {
      setLegacyTheme(newCanvas.theme)
    }

    resetCanvasHistory()

    const overrides = newCanvas?.content?.overrides ? simpleDeepClone(newCanvas.content).overrides : {}
    activeCanvas.value = newCanvas
    canvasContent.value = data as ComponentNode[]
    sectionComponentOverrides.value = overrides as SectionComponentOverrides

    // Initialize client-side home page settings from server state
    canvasHomePage.value = newCanvas?.content?.home_page ? { ...newCanvas.content.home_page } : null
  } catch (e) {
    devLog(`Failed to load canvas ${newCanvas.id}} with content`, newCanvas.content, e)
  }
}

async function updateFileDataDictForContentGrid(nodeType: string, updatedSections?: CanvasSection[]): Promise<void> {
  if (nodeType !== 'ContentGrid') {
    return
  }

  const allCanvasContent = [...canvasContent.value]

  if (updatedSections) {
    updatedSections.forEach((section) => {
      if (section.content?.data) {
        allCanvasContent.push(...section.content.data)
      }
    })
  } else if (activeCanvas.value?.sections) {
    activeCanvas.value.sections.forEach((section) => {
      if (section.content?.data) {
        allCanvasContent.push(...section.content.data)
      }
    })
  }

  try {
    await updateFileDataDict(allCanvasContent)
  } catch (error) {
    console.error('Failed ContentGrid file data:', error)
  }
}

async function updateFileDataDict(data: ComponentNode[]): Promise<void> {
  const fileIds = new Set<string>()

  const collectFileIds = (nodes: ComponentNode[]) => {
    nodes.forEach((node) => {
      if (node.type === 'ContentGrid') {
        const contentGridData = node.data as ContentGridProps
        contentGridData.items?.forEach((item) => {
          if (item.file?.id) {
            fileIds.add(item.file.id)
          }
        })
      }
      if (node.children) collectFileIds(node.children)
    })
  }
  collectFileIds(data)

  if (fileIds.size === 0) {
    return
  }

  if (!fetchFile.value) {
    return
  }

  try {
    const filePromises = Array.from(fileIds).map(async (fileId) => {
      try {
        const file = await fetchFile.value?.(fileId)
        return file
      } catch (error) {
        return null
      }
    })
    const files = await Promise.all(filePromises)

    // Update the file data cache
    files.forEach((file: any) => {
      if (file?.id) {
        fileDataCache.value.set(file.id, file)
      }
    })
  } catch (error) {
    console.error('Failed ContentGrid file data:', error)
  }
}

const setInCall = (value?: boolean | undefined) => {
  if (mode.value === 'sharing') return

  inCall.value = value
}

const addComponentAtParentId = (
  type: ComponentTypes,
  parentId: ComponentNode['id'] | null = null,
  afterSiblingId: ComponentNode['id'] | null = null,
  withData: ComponentNode['data'] | null = null,
  withChildren: ComponentNode['children'] = [],
  withExtraNodeData: Partial<ComponentNode> = {},
) => {
  const { addCanvasHistoryItem } = useCanvasHistory()

  const { newContent, newComponentId } = addCanvasComponent(canvasContent.value, {
    type,
    parentId,
    afterSiblingId,
    withData,
    withChildren,
    withExtraNodeData,
  })
  if (!newComponentId) return
  canvasContent.value = newContent
  justAddedComponentId.value = newComponentId // the actual component is responsible for resetting this
  setComponentSelectedMode(newComponentId) // select the new component

  addCanvasHistoryItem({
    action: CanvasHistoryAction.ADD,
    componentType: type,
  })
}

const removeComponent = (id: ComponentNode['id'], sectionId?: CanvasRetrieve['id']) => {
  if (!sectionId) {
    return removeComponentById(id)
  }

  return overrideSectionComponentData({
    usedInSectionId: sectionId,
    componentId: id,
    remove: true,
  })
}

const removeComponentById = (id: ComponentNode['id']) => {
  const { addCanvasHistoryItem } = useCanvasHistory()
  const component = componentNodesById.value[id]

  if (!component) return
  canvasContent.value = filterTreeBy(canvasContent.value, (n) => n.id !== id)

  addCanvasHistoryItem({
    action: CanvasHistoryAction.REMOVE,
    componentType: component.type,
  })
}

const updateNodeData = (data: ComponentNode['data'], id: ComponentNode['id'], sectionId?: CanvasRetrieve['id']) => {
  if (!sectionId) {
    return updateNodeDataById(id, data)
  }

  return overrideSectionComponentData({
    usedInSectionId: sectionId,
    componentId: id,
    componentData: data,
  })
}

const updateNodeDataById = (id: ComponentNode['id'], newData: ComponentNode['data']) => {
  let node: ComponentNode | null = null
  if (enableImprovedCanvasNodeUpdating.value) {
    node = findNodeInTreeById(canvasContent.value, id)

    if (node) {
      node.data = newData
    }
  } else {
    const newCanvasContent = simpleDeepClone(canvasContent.value)
    node = findNodeInTreeById(newCanvasContent, id)

    if (node) {
      node.data = newData
      canvasContent.value = newCanvasContent
    }
  }

  if (node) {
    updateFileDataDictForContentGrid(node.type)
  }
  // If node not found in main canvas content, check if it's inside a section
  else if (activeCanvas.value?.sections?.length) {
    let foundInSection = false

    // Create a copy of the sections to modify
    const newSections = simpleDeepClone(activeCanvas.value.sections)

    // Iterate through sections to find the node
    newSections.forEach((section) => {
      if (!section.content?.data?.length || foundInSection) return

      // Define a recursive function to find and update a node in the section content
      const findAndUpdateNodeInSection = (nodes: ComponentNode[]): boolean => {
        for (const node of nodes) {
          if (node.id === id) {
            // Found the node - update its data
            node.data = newData

            foundInSection = true
            return true
          }

          // Check children recursively
          if (node.children?.length) {
            const found = findAndUpdateNodeInSection(node.children)
            if (found) return true
          }
        }

        return false
      }

      // Process the section's content
      findAndUpdateNodeInSection(section.content.data)
    })

    if (foundInSection) {
      // Update the active canvas with the modified sections
      const updatedCanvas = {
        ...activeCanvas.value,
        sections: newSections,
      }
      setActiveCanvas(updatedCanvas)

      const updatedNode = findNodeInTreeById(
        newSections.flatMap((s) => s.content?.data || []),
        id,
      )
      if (updatedNode) {
        updateFileDataDictForContentGrid(updatedNode.type, newSections)
      }
      return
    }
  }
}

const patchNodeById = (id: ComponentNode['id'], patch: Partial<ComponentNode>) => {
  const node = findNodeInTreeById(canvasContent.value, id)

  if (!node) {
    console.warn(`Could not find node with id: ${id}`)
    return
  }

  Object.assign(node, patch)
}

const deepMergeNodeById = (id: ComponentNode['id'], patch: Partial<ComponentNode>) => {
  const node = findNodeInTreeById(canvasContent.value, id)

  if (!node) {
    devLog(`Could not find node with id: ${id}`)
    return
  }

  merge(node, patch)
}

const patchCanvasContentByCondition = (condition: (node: ComponentNode) => boolean, patch: Partial<ComponentNode>) => {
  const newCanvasContent = simpleDeepClone(canvasContent.value)
  const patchNode = (node: ComponentNode) => {
    if (condition(node)) {
      Object.assign(node, patch)
    }
    node.children?.forEach(patchNode)
  }
  newCanvasContent.forEach(patchNode)
  canvasContent.value = newCanvasContent
}

const resetChildrenOfNodeById = (id: ComponentNode['id']) => {
  const newCanvasContent = simpleDeepClone(canvasContent.value)
  const node = findNodeInTreeById(newCanvasContent, id)
  if (!node) {
    devLog(`Could not find node with id: ${id}`)
    return
  }
  node.children = []
  canvasContent.value = newCanvasContent
}

const duplicate = (componentId: ComponentNode['id']) => {
  const component = findNodeInTreeById(canvasContent.value, componentId)
  const parent = findParentByNodeId(canvasContent.value, componentId)

  if (!component) {
    return
  }

  addComponentAtParentId(
    component.type,
    parent?.id ?? null,
    component.id,
    component.data,
    component.children?.length ? regenerateTreeIds(component.children) : undefined,
    component,
  )
}

const activateNavigationItem = (id: string, sectionId?: string, previewMode?: boolean, index?: number) => {
  const found = activeNavigationItems.value.find((item) => item.id === id && item.sectionId === sectionId)

  if (found) {
    found.previewMode = previewMode
    return
  }

  activeNavigationItems.value.push({ sectionId, id, previewMode, index })
  appEmit('activeNavigationItemChange', activeNavigationItems.value[0])
}

const deactivateNavigationItem = (id: string, sectionId?: string, previewMode?: boolean, itemIndex?: number) => {
  const index = activeNavigationItems.value.findIndex(
    (item) =>
      item.id === id && item.sectionId === sectionId && item.previewMode === previewMode && item.index === itemIndex,
  )

  if (index < 0) return
  activeNavigationItems.value.splice(index, 1)
  appEmit('activeNavigationItemChange', activeNavigationItems.value[0])
}

// DEBUG: check false positives if needed
// watch(
//   () => [isContentSameAsSaved.value, areOverridesSameAsSaved.value],
//   ([isContentSame, areOverridesSame]) => {
//     if (!isContentSame) {
//       console.log(
//         `Canvas content is not the same as saved`,
//         deepDiff(activeCanvas.value?.content?.data ?? {}, canvasContent.value ?? {}),
//       )
//     } else {
//       console.log(`Canvas content is the same as saved`)
//     }
//     if (!areOverridesSame) {
//       console.log(
//         `Canvas overrides are not the same as saved`,
//         deepDiff(activeCanvas.value?.content?.overrides ?? {}, sectionComponentOverrides.value ?? {}),
//       )
//     } else {
//       console.log(`Canvas overrides are the same as saved`)
//     }
//   },
//   { deep: true, immediate: true },
// )

const isDefaultDisplayMode = computed(() => displayMode.value === 'default')
const isPrintDisplayMode = computed(() => displayMode.value === 'print')
const isHomeMode = computed(() => mode.value === 'home')
const setCanvasContent = ({ content, component }: { content: ComponentNode[]; component?: ComponentNode | null }) => {
  if (component) {
    component.children = content
  } else {
    canvasContent.value = content
  }
}

const setCanvasMetadata = (metadata: CanvasMetadataRetrieve['config'] | null) => {
  canvasMetadata.value = metadata
}

function exitFullscreen(options: { skipJsApiEvent?: boolean } = {}) {
  isFullscreen.value = false
  appEmit('toggleFullscreen', isFullscreen.value, options)
}

function enterFullscreen(options: { skipJsApiEvent?: boolean } = {}) {
  isFullscreen.value = true
  appEmit('toggleFullscreen', isFullscreen.value, options)
}

// `pitcher_is_original_content` is added from ppt-conversion-selector and we change it to false on explicit user save if metadata exist
function getMetadataIfIsOriginalContent() {
  const currentMetadata = activeCanvas.value?.metadata ?? {}

  return currentMetadata?.pitcher_is_original_content
    ? { metadata: { ...currentMetadata, pitcher_is_original_content: false } }
    : {}
}

async function saveCanvasContent({ showToast }: { showToast: boolean } = { showToast: true }) {
  if (isSameAsSaved.value) {
    return Promise.resolve()
  } else if (!activeCanvas.value) {
    return Promise.reject(new Error('No active canvas'))
  }

  const c = await save(
    activeCanvas.value.id,
    {
      content: {
        ...(activeCanvas.value?.content ?? {}),
        data: canvasContent.value,
        overrides: sectionComponentOverrides.value,
        home_page: canvasHomePage.value,
      },
      ...getMetadataIfIsOriginalContent(),
      include_expired_files: isAdmin.value ? true : undefined,
    },
    showToast,
  )

  if (c && c.id === activeCanvas.value?.id) {
    // Process the canvas data BEFORE setting it as active
    const processedCanvas = processCanvasForSectionThemeOverride(c, maintainSectionTheme.value)

    setActiveCanvas({
      ...processedCanvas,
      content: {
        ...processedCanvas.content,
        // avoid retriggering of PATCHes due to overrides mismatch
        overrides: merge(sectionComponentOverrides.value ?? {}, processedCanvas.content?.overrides ?? {}),
      },
    })

    // Clear manually changed theme assets after successful save
    clearManuallyChangedThemeAssets()
  } else {
    console.warn('[saveCanvasContent] Patch does not concern active canvas:', {
      activeCanvasId: activeCanvas.value?.id,
      patchedCanvas: c,
    })
  }
}

async function save(
  canvasId: Parameters<NonNullable<typeof onSave.value>>[0],
  canvas: Parameters<NonNullable<typeof onSave.value>>[1],
  showToast?: Parameters<NonNullable<typeof onSave.value>>[2],
) {
  if (!onSave.value) {
    throw new Error('Missing `onSave` function')
  }

  const data = { ...canvas, ...('context' in canvas ? { context: getSafeContext(canvas.context) } : {}) }

  return onSave.value(canvasId, data, showToast)
}

async function saveCanvasContext(context: any, { showToast }: { showToast?: boolean } = { showToast: false }) {
  if (!activeCanvas.value) {
    return Promise.reject(new Error('No active canvas'))
  }

  const c = await save(
    activeCanvas.value.id,
    {
      context,
    },
    showToast,
  )

  if (c && c.id === activeCanvas.value?.id) {
    setActiveCanvas({
      ...activeCanvas.value,
      ...c,
      context: {
        ...(activeCanvas.value?.context ?? {}),
        ...c.context,
      },
    })
  } else {
    console.warn('[saveCanvasContext] Patch does not concern active canvas:', {
      activeCanvasId: activeCanvas.value?.id,
      patchedCanvas: c,
    })
  }
}

async function saveCanvasContentWithContext(
  context: any,
  { showToast }: { showToast?: boolean } = { showToast: true },
  extraMetadata?: Record<string, any>,
) {
  if (!activeCanvas.value) {
    return Promise.reject(new Error('No active canvas'))
  }

  const baseWrapper = getMetadataIfIsOriginalContent() as any
  const payload: any = {
    content: {
      ...(activeCanvas.value?.content ?? {}),
      data: canvasContent.value,
      overrides: sectionComponentOverrides.value,
      home_page: canvasHomePage.value,
    },
    context,
    ...(baseWrapper ?? {}),
  }

  if (extraMetadata && Object.keys(extraMetadata).length > 0) {
    payload.metadata = {
      ...(payload.metadata ?? {}),
      ...extraMetadata,
    }
  }

  // Important: We SKIP theme preservation for sections here
  // This is key because we want to keep whatever theme changes were made
  // by the theme editor, rather than restoring section themes
  const c = await save(activeCanvas.value.id, payload, showToast)

  if (c && c.id === activeCanvas.value?.id) {
    // Update the canvas content with merged data
    // But DO NOT attempt to process or restore section themes
    // This prevents the theme updates from being overwritten
    setActiveCanvas({
      ...c,
      content: {
        ...(c.content ?? {}),
        home_page: canvasHomePage.value, // Preserve our home page value
      },
    })

    // After saving and updating the active canvas, we need to reload themes for visible sections
    // This is similar to what happens in SectionList.raw.vue when sections become visible
    if (maintainSectionTheme.value && c.sections?.length) {
      // For each section that should have its data loaded
      c.sections.forEach((section) => {
        // Don't process if the section inherits theme from parent
        if (section.theme?.inherit_theme_from_parent) return

        // Apply section typography and theme
        if (areThemesEnabled.value) {
          // First load section themes (typography)
          loadSectionThemes(c.sections, section)

          // Only if section has its own theme, load section theme assets
          if (section.theme) {
            // Create an adapter function to match the expected signature
            const updateNodeAdapter = (
              nodeId: string,
              props: MultimediaProps,
              directNodeUpdate?: {
                node: ComponentNode
                section: CanvasRetrieve['sections'][number]
              },
            ) => {
              return updateNodeWithThemeAsset(
                nodeId,
                props,
                directNodeUpdate,
                updateNodeDataById,
                cloneDeep(componentNodesById.value) as any,
                activeCanvas.value,
              )
            }

            // Update section multimedia components with theme assets
            loadSectionThemesAssets(c.sections, updateNodeAdapter, section)
          }
        }
      })
    }
  } else {
    devLog('[saveCanvasContentWithContext] Patch does not concern active canvas:', {
      activeCanvasId: activeCanvas.value?.id,
      patchedCanvas: c,
    })
  }
}

const setIsEditMode = (value: boolean) => {
  isEditMode.value = value
}

const navigateToHome = (skipSetPreviousLocation?: boolean) =>
  navigateToHomeHelper({
    homePageConfig: activeCanvas.value?.content?.home_page,
    canvasContent: canvasContent.value,
    componentNodesById: componentNodesById.value,
    skipSetPreviousLocation: skipSetPreviousLocation,
  })

const updateHomePageSettings = (homePage: { component_id: string; section_id?: string | null } | null) => {
  canvasHomePage.value = homePage
}

const mergeWithArrayReplacement = (...sources: any[]) =>
  mergeWith({}, ...sources, (_objValue: any, srcValue: any) => {
    if (Array.isArray(srcValue)) {
      return srcValue // Replace arrays instead of merging
    }

    return undefined // Let lodash handle other types
  })

const mergeIntoActiveContext = (contextUpdate: Record<string, any>) => {
  if (!activeCanvas.value) {
    console.warn('[mergeIntoActiveContext] No active canvas to update context')
    return
  }

  const currentContext = activeCanvas.value.context ?? {}
  const mergedContext = mergeWithArrayReplacement(currentContext, contextUpdate)

  activeCanvas.value = {
    ...activeCanvas.value,
    context: mergedContext,
  }
}

function getMaxWidth(
  pitcherSettings: OrganizationSettings & InstanceSettings,
  enableScaleContent?: boolean,
  enableCanvasPdfDimensions?: boolean,
) {
  if (isPrintDisplayMode.value) {
    let pdfWidth: string | number | undefined

    // Prioritize canvas-specific PDF dimensions if the flag is enabled
    if (enableScaleContent && enableCanvasPdfDimensions && activeCanvas.value?.theme?.pdf_dimensions?.width) {
      pdfWidth = activeCanvas.value.theme.pdf_dimensions.width
    } else if (enableScaleContent && pitcherSettings.default_pdf_page_dimensions?.width) {
      pdfWidth = pitcherSettings.default_pdf_page_dimensions.width
    }

    const defaultWidthPx = pdfWidth ? convertToPixels(pdfWidth) : undefined
    return defaultWidthPx ? `${defaultWidthPx}px` : isFluid.value ? undefined : `${CANVAS_WIDTH}px`
  }
  return isFluid.value ? undefined : `${CANVAS_WIDTH}px`
}

const getParentType = (nodeId: Ref<ComponentNode['id'] | null | undefined>) => {
  return computed(() => {
    if (!nodeId.value || !canvasContent.value) return undefined
    const parent = findParentByNodeId(canvasContent.value, nodeId.value)
    return parent?.type
  })
}

export default function useCanvas() {
  return {
    activeCanvas,
    activeCanvasSections,
    activeEditNode,
    activeNavigationItems,
    activeSettingsNode,
    addMode,
    areTokenConstraintsActive,
    areThemesEnabled,
    areThemeAssetsEnabled,
    canvasContent,
    canvasHomePage,
    canvasMetadata,
    fileDataCache,
    componentEditMode,
    parentIdByNodeId,
    componentNodesById,
    componentSelectedMode,
    componentSettingsMode,
    contentRef,
    currentEmbedLocation,
    displayMode,
    endPage,
    fetchFile,
    getEnv,
    inCall,
    isAdmin,
    isCRM,
    isCanvas,
    isCanvasTemplate,
    isDefaultDisplayMode,
    isDsr,
    isEditMode,
    isFullscreen,
    isHomeMode,
    isImpact,
    isCanvasPreviewMode,
    isPrintDisplayMode,
    isRedirectEvent,
    isSameAsSaved,
    isContentSameAsSaved,
    areOverridesSameAsSaved,
    isHomePageSameAsSaved,
    isSection,
    isTemplate,
    isBlock,
    isViewOnlyMode,
    justAddedComponentId,
    listFiles,
    isSetListFilesApi,
    listRecommendedFiles,
    mode,
    myUserId,
    navigationItems,
    onComponentEnter,
    onComponentExit,
    onAppEnter,
    onAppExit,
    onPageEnter,
    onPageExit,
    onDownloadCanvas,
    onDownloadFile,
    onExit,
    onFileOpen,
    onFileEnter,
    onFileExit,
    onFilePageEnter,
    onFilePageExit,
    onCollectionPlayerContentChange,
    save,
    onSave: (
      canvasId: Parameters<NonNullable<CanvasBuilderProps['onSave']>>[0],
      canvasData: Parameters<NonNullable<CanvasBuilderProps['onSave']>>[1],
      showToast?: Parameters<NonNullable<CanvasBuilderProps['onSave']>>[2],
    ) => {
      console.warn('Function deprecated - use `save` instead of `onSave` function')
      return onSave.value?.(canvasId, canvasData, showToast)
    },
    temporaryContextKeys,
    setTemporaryContextKeys,
    onSfdcAccountSearch,
    onTrackShare,
    renderPageAsImageUrl,
    sectionComponentNodesBySectionId,
    sectionComponentOverrides,
    selectedComponentType,
    selectedComponentParentType,
    activeEditNodeParentType,
    selectedComponentParent,
    share,
    singletonsInCanvasMap,
    startPage,
    visibleCollectionPlayerRefs,
    builderCache,
    writeablePitcherSettings: pitcherSettings,
    pitcherSettings: readonly(pitcherSettings),
    initialize,
    activateNavigationItem,
    deactivateNavigationItem,
    addComponentAtParentId,
    duplicate,
    enterFullscreen,
    exitFullscreen,
    overrideSectionComponentData,
    patchCanvasContentByCondition,
    patchNodeById,
    removeComponent,
    removeComponentById,
    resetChildrenOfNodeById,
    setActiveCanvas,
    setAddMode,
    setCanvasContent,
    setCanvasMetadata,
    setComponentEditMode,
    setComponentSelectedMode,
    setComponentSettingsMode,
    setInCall,
    setIsEditMode,
    updateActiveCanvasProperties,
    mergeIntoActiveContext,
    updateHomePageSettings,
    updateNodeData,
    updateNodeDataById,
    deepMergeNodeById,
    cleanup,
    saveCanvasContent,
    saveCanvasContext,
    saveCanvasContentWithContext,
    navigateToHome,
    processCanvasNodesForDisplay,
    updateMultimediaNodeWithThemeAsset: (
      nodeId: string,
      multimediaProps: MultimediaProps,
      directNodeUpdate?: {
        node: ComponentNode
        section: CanvasRetrieve['sections'][number]
      },
    ) =>
      updateNodeWithThemeAsset(
        nodeId,
        multimediaProps,
        directNodeUpdate,
        updateNodeDataById,
        cloneDeep(componentNodesById.value) as any,
        activeCanvas.value,
      ),
    findNodeInSection: (sectionId: string, nodeId: string) =>
      findNodeInSectionById(activeCanvas.value, sectionId, nodeId),
    updateNodeInSection: (sectionId: string, nodeId: string, newData: ComponentNode['data']) =>
      updateNodeInSectionById(activeCanvas.value, sectionId, nodeId, newData, setActiveCanvas),
    getMaxWidth,
    getParentType,
    onWizardEnter,
    onWizardExit,
    contentAnnotation,
    getSafeContext,
    watchCanvas,
    mergeWithArrayReplacement,
  }
}
